import { toRaw } from 'vue';
import { storeToRefs } from 'pinia';
import deepmerge from 'deepmerge';

import { depatternize } from '@formatters/patternize';

import invalidateCPF from '@validators/cpf';
import invalidateCNPJ from '@validators/cnpj';

import axios from '@js/clients/axios';
import { encrypt, decrypt } from '@js/clients/crypto';
import { BANK_EXCLUSION } from '@js/constants/banks';
import { vehicleFindCallback, vehicleNameCallback } from '@js/callbacks/vehicle';

import useAuthStore from '@/stores/auth';

import zipCode from '@/views/Customers/javascripts/zip';
import { vehicle as brandNormalizer } from '@/views/Simulations/javascripts/normalizers';

import filled from '@/components/Form/javascripts/filled';
import fieldName from '@/components/Form/javascripts/name';
import { VISIBLE_OPTIONS } from '@/components/Form/javascripts/options';

import SCHEMA, { LEAD_REQUIRED, MINIMUM_FIELDS } from '../SCHEMA';

import CONSTANTS from './constants';
import { normalizeLead } from './normalizer';

import GROUPS from './groups';

const VEHICLE_FIELDS = {
  NAME: 'simulation[vehicle][name]',
  MANUFACTURE: 'simulation[vehicle][manufacture_year]',
  MODEL: 'simulation[vehicle][model_year]',
  IDENTIFIER: 'simulation[vehicle][credere_vehicle_model_id]',
};

const authStore = useAuthStore();
const { scope, user } = storeToRefs(authStore);

function vehicleManufacture(props, planter, name) {
  if (!planter._INITIALIZED) {
    return {};
  }

  planter
    .leaves
    .value['simulation[vehicle][model_year]']
    .validations
    .find((i) => i.type === 'greater_than_or_equal_to')
    .count = planter.values.value['simulation[vehicle][manufacture_year]'];

  if (filled(planter.values.value['simulation[vehicle][model_year]'])) {
    planter._COMPOSABLES.invalidate('simulation[vehicle][model_year]');
  }

  return vehicleFind(props, planter, name);
}

function vehicleFind(props, planter, name) {
  return vehicleFindCallback(VEHICLE_FIELDS, props, planter, name);
}

function vehicleName(props, planter, name, __value) {
  return vehicleNameCallback(VEHICLE_FIELDS, props, planter, name, __value);
}

function vehicleRuling(props, planter) {
  if (
    !planter._INITIALIZED
    || !props.settings.simulation?.vehicle?._enable_price_commercial_control
  ) {
    return {};
  }

  const store = planter.values.value['simulation[store_id]'];

  if (!store) {
    return {};
  }

  function success(response) {
    const price = response.data.vehicle_prices?.[0]?.default_price_cents;
    const minimum = response.data.vehicle_prices?.[0]?.min_price_cents;

    const validation = planter
      .leaves
      .value['simulation[vehicle][asset_value]']
      .validations
      .find((i) => i.type === 'greater_than');

    if (validation) {
      validation.count = minimum || 0;
    }

    return {
      'simulation[vehicle][asset_value]': price || 0,
    };
  }

  function failure(error) {
    /* eslint-disable no-console */
    console.log('VEHICLE_PRICES/LIST FAILURE');
    console.error(error);
    /* eslint-enable no-console */

    return {};
  }

  return {
    loading: ['simulation[vehicle][asset_value]'],
    promise: axios
      .get('/vehicle_prices', {
        params: {
          store_id: store,
          vehicle_model_id: planter.values.value['simulation[vehicle][credere_vehicle_model_id]'],
        },
      })
      .then(success)
      .catch(failure),
  };
}

function vehicleSearch(props, planter, name) {
  const plate = (planter.values.value[name] || '').replace(/[^a-z0-9]/gi, '');

  if (!plate) {
    return {};
  }

  function success(response) {
    const { vehicle } = response.data;
    const models = vehicle?.vehicle_models;
    const first = models?.[0];

    planter.leaves.value[VEHICLE_FIELDS.IDENTIFIER].options = [];

    if (!first) {
      return {};
    }

    planter.leaves.value[VEHICLE_FIELDS.IDENTIFIER].options = models
      .map((i) => ({
        label: `${i.name} - ${i.version} - ${i.fuel.name}`,
        value: i.id,
      }));

    const result = {
      [VEHICLE_FIELDS.MANUFACTURE]: vehicle.manufacture_year,
      [VEHICLE_FIELDS.MODEL]: vehicle.model_year,
      [VEHICLE_FIELDS.NAME]: brandNormalizer(first).value,
      'simulation[vehicle][zero_km]': false,
    };

    if (models.length === 1) {
      result[VEHICLE_FIELDS.IDENTIFIER] = first.id;

      setTimeout(() => { planter.values.value[VEHICLE_FIELDS.IDENTIFIER] = { blur: true }; }, 100);
    }

    return result;
  }

  function failure(error) {
    /* eslint-disable no-console */
    console.log('VEHICLES/PLATE FAILURE');
    console.error(error);
    /* eslint-enable no-console */

    return {};
  }

  return {
    loading: [
      ...Object.values(VEHICLE_FIELDS),
      'simulation[vehicle][zero_km]',
    ],
    promise: axios
      .get(`/vehicles/license_plate/${plate}`)
      .then(success)
      .catch(failure),
  };
}

export function leadStorageAddress(cpf) {
  return `credere:public_simulator:lead:${depatternize(cpf)}`;
}

export function leadStorageKey(cpf) {
  return `${depatternize(cpf)}-|<3y`;
}

export function leadStorageSet(cpf, parsed) {
  if (!scope.value.includes('embedded')) {
    return;
  }

  try {
    window.localStorage.setItem(
      leadStorageAddress(cpf),
      encrypt(JSON.stringify(parsed), leadStorageKey(cpf)),
    );
  } catch (err) { /* */ }
}

export function leadStorageGet(cpf) {
  const LEAD = { lead: {} };

  if (!scope.value.includes('embedded')) {
    return LEAD;
  }

  try {
    let stored = window.localStorage.getItem(leadStorageAddress(cpf));

    if (stored) {
      stored = JSON.parse(decrypt(stored, leadStorageKey(cpf)));

      normalizeLead(stored.lead);

      return stored;
    }

    return LEAD;
  } catch (err) {
    return LEAD;
  }
}

function leadRequiredFallback(props, fields, requirements, available = [], name = 'lead') {
  return Object.keys(fields).reduce((reduced, key) => {
    const field = fieldName(name, key);

    if (fields[key].fields) {
      reduced[key] = leadRequiredFallback(
        props,
        fields[key].fields,
        requirements?.[key],
        available,
        field,
      );
    } else {
      let banks = [];
      const inclusion = props.banksInclusion;

      if (MINIMUM_FIELDS.includes(field)) {
        banks = available.length ? BANK_EXCLUSION(inclusion, available) : inclusion;
      } else if (available.length) {
        banks = BANK_EXCLUSION(inclusion, requirements?.[key]);
      } else {
        banks = inclusion;
      }

      reduced[key] = banks;
    }

    return reduced;
  }, {});
}

function leadRequired(props, planter, requirements, name = 'lead', settings = null) {
  Object.keys(requirements || {}).forEach((key) => {
    const _settings = (settings || props.settings)?.[name];
    const _setting = _settings?.[`_${key}`];

    const field = fieldName(name, key);

    if (planter.leaves.value[field] || VISIBLE_OPTIONS.includes(_setting?.visibility)) {
      planter.leaves.value[field].rules = LEAD_REQUIRED(requirements[key]);
    }

    if (Array.isArray(requirements[key]) && requirements[key].every((j) => typeof j === 'string')) {
      return;
    }

    leadRequired(props, planter, requirements[key], field, _settings);
  });
}

function storeSettings(props, planter) {
  const {
    values,
  } = planter;

  const id = parseInt(values.value['simulation[store_id]'], 10);
  const store = props.domains.stores?.find((i) => i.id === id);
  const role = user?.value?.role?.identifier;

  const customSettings = props.settings.simulation?._condition?._enable_custom_settings && (
    store?.allow_commercial_control_customization_by_sellers
    || !CONSTANTS.ROLES.ALLOW_COMMERCIAL_CONTROL_CUSTOMIZATION_BY_SELLERS.includes(role)
  );

  const customFields = true
    && props.settings.simulation?._condition?._enable_exclusive_bank_fields
    && store?.show_exclusive_bank_fields_on_simulations_form;

  const search = props.settings.simulation?.vehicle?._enable_vehicle_search_by_license_plate
    && store?.allow_vehicle_search_by_license_plate;

  const result = {
    'simulation[vehicle][_enable_vehicle_search_by_license_plate]': search,
    'simulation[_condition][_enable_custom_settings]': customSettings,
    'simulation[_condition][_enable_exclusive_bank_fields]': customFields,
  };

  if (values.value['simulation[_condition][_custom_settings_toggler]'] && !customSettings) {
    result['simulation[_condition][_custom_settings_toggler]'] = customSettings;
  }

  return result;
}

function leadStore(props, planter) {
  return leadCPF(props, planter, 'lead[cpf_cnpj]');
}

function leadCPF(props, planter, name) {
  const {
    entity,

    leaves,
    values,
    properties,
  } = planter;

  const storeID = parseInt(values.value['simulation[store_id]'], 10);
  const store = props.domains.stores?.find((i) => i.id === storeID);

  if (!store?.id) {
    /* eslint-disable no-console */
    console.log('LEADS/READ FAILURE');
    console.error('Nenhuma loja encontrada para o ID informado. Por favor, verifique suas configurações e tente novamente.');
    /* eslint-enable no-console */

    return {};
  }

  const cpf = depatternize(values.value[name]);
  const original = depatternize(props.settings?.lead?.cpf_cnpj);

  function requiredSuccess(response) {
    const { lead, requirements } = response.data.data;

    leadRequired(
      props,
      planter,
      leadRequiredFallback(props, SCHEMA.lead.fields, requirements, requirements?.cpf_cnpj),
    );

    if (!lead?.id) {
      return requiredFailure('Nenhum "Lead" encontrado para o CPF informado. Mostrando formulário em branco.', true);
    }

    entity.value = {
      simulation: toRaw(entity.value.simulation),
      lead: deepmerge(leadStorageGet(cpf)?.lead, lead),
      _clear: cpf === original ? null : 'lead',
    };

    return {};
  }

  function requiredFailure(error, forced) {
    /* eslint-disable no-console */
    console.log('LEADS/READ FAILURE');
    console.error(error);
    /* eslint-enable no-console */

    if (forced !== true) {
      leadRequired(
        props,
        planter,
        leadRequiredFallback(props, SCHEMA.lead.fields, undefined, []),
      );
    }

    entity.value = {
      simulation: toRaw(entity.value.simulation),
      lead: deepmerge(leadStorageGet(cpf)?.lead, { cpf_cnpj: cpf }),
      _clear: cpf === original ? null : 'lead',
    };

    return {};
  }

  function integratedSuccess(response) {
    store._banks = (response.data?.banks || []).map((i) => i.code);

    return {};
  }

  function integratedFailure(error) {
    /* eslint-disable no-console */
    console.log('BANKS/INTEGRATED FAILURE');
    console.error(error);
    /* eslint-enable no-console */

    return {};
  }

  return {
    partial: [
      storeSettings(props, planter),
      {},
    ],
    loading: [
      Object
        .keys(leaves.value)
        .filter((i) => true
          && leaves.value[i].group?.key === GROUPS.ADVANCED?.key
          && !properties.value[i].hidden),
      Object
        .keys(leaves.value)
        .filter((i) => true
          && /^lead\[/.test(i)
          && !properties.value[i].hidden),
    ],
    promise: [
      store._banks
        ? Promise.resolve({})
        : axios
          .get(`/banks/integrated?store_id=${storeID}`)
          .then(integratedSuccess)
          .catch(integratedFailure),
      (!cpf || (invalidateCPF(cpf) && invalidateCNPJ(cpf)))
        ? Promise.resolve({})
        : axios
          .get(`/banks_api/leads/${cpf}/required_fields`, { headers: { 'store-id': storeID } })
          .then(requiredSuccess)
          .catch(requiredFailure),
    ],
  };
}

export default {
  'simulation[vehicle][name]': vehicleName,
  'simulation[vehicle][manufacture_year]': vehicleManufacture,
  'simulation[vehicle][model_year]': vehicleFind,

  'simulation[vehicle][credere_vehicle_model_id]': vehicleRuling,
  'simulation[vehicle][_vehicle_search_by_license_plate]': vehicleSearch,

  'simulation[store_id]': leadStore,
  'lead[cpf_cnpj]': leadCPF,

  'lead[address][zip_code]': zipCode('lead[address]', true),
};
